import { faCaretDown, faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Label } from '@playcastdotio/Shadcn';
import clsx from 'clsx';
import { useRef, useState } from 'react';

type BeaconSourceChoice = {
  label: string;
  value: string;
};

type BeaconSource = {
  label: 'Desktop' | 'Cameras' | 'Window Capture';
  choices: BeaconSourceChoice[];
};

type BeaconDisplayPickerProps = {
  sources: BeaconSource[];
  selectedLabel: string;
  onSelectDesktop: () => void;
  onSelectSurfMode: () => void;
  onSelectDesktopCapture: () => void;
  onSelectCamera: (deviceId: string) => void;
  onSelectWindowCapture: (processId: number) => void;
  onRefreshApps: () => void;
};

export const BeaconDisplayPicker = ({
  sources,
  selectedLabel,
  onSelectDesktop,
  onSelectSurfMode,
  onSelectDesktopCapture,
  onSelectCamera,
  onSelectWindowCapture,
  onRefreshApps,
}: BeaconDisplayPickerProps) => {
  const [isSourceOpen, setIsSourceOpen] = useState(false);
  const [selectedSource, setSelectedSource] = useState<BeaconSource['label'] | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleSourceToggle = () => {
    setIsSourceOpen((prev) => !prev);
    setTimeout(() => dropdownRef.current?.focus(), 0);
    onRefreshApps();
  };

  const handleSourceChange = (source: BeaconSource['label']) => {
    setSelectedSource((prev) => (source === prev ? null : source));
  };

  const handleSelectedChoice = (sourceLabel: BeaconSource['label'], value: string) => {
    setIsSourceOpen(false);
    switch (sourceLabel) {
      case 'Desktop':
        if (value === 'followMouse') onSelectSurfMode();
        else if (value === 'desktopCapture') onSelectDesktopCapture();
        else onSelectDesktop();
        break;
      case 'Window Capture':
        onSelectWindowCapture(parseInt(value));
        break;
      case 'Cameras':
        onSelectCamera(value);
        break;
    }
  };

  return (
    <div className='relative'>
      <button
        className={clsx(
          'theme-midnight:shadow-[inset_2px_3px_2px_#A9E8EA,inset_-2px_-3px_2px_#2D618C] bg-primary hover:bg-primary/60 flex w-full cursor-pointer items-center justify-between rounded-md p-2',
        )}
        onMouseDown={handleSourceToggle}
      >
        <span className='truncate'>{selectedLabel}</span>
        <FontAwesomeIcon icon={faCaretDown} className='h-4 w-4 flex-shrink-0' />
      </button>
      <div
        ref={dropdownRef}
        tabIndex={0}
        className={clsx(
          isSourceOpen ? 'flex' : 'hidden',
          'absolute z-50 w-full translate-x-0 flex-col rounded-md bg-gray-600 shadow md:translate-x-1',
        )}
        onBlur={() => setIsSourceOpen(false)}
      >
        {sources.map((source) => (
          <div
            key={source.label}
            className='border-gray-500/60 first:rounded-t-md last:rounded-b-md [&:not(:last-child)]:border-b'
            onClick={() => handleSourceChange(source.label)}
          >
            <div className='flex w-full cursor-pointer items-center justify-between bg-inherit p-2'>
              <Label className={clsx(selectedSource === source.label ? 'text-amber-400' : 'text-midnight-white', 'text-md cursor-pointer')}>
                {source.label}
              </Label>
              <FontAwesomeIcon
                icon={faChevronDown}
                className={clsx(selectedSource === source.label ? 'rotate-180' : 'rotate-0', 'h-3 w-3 transition-all')}
              />
            </div>
            <div
              className={clsx(
                source.label === selectedSource ? 'max-h-[200px] overflow-y-auto' : 'max-h-0 overflow-hidden',
                'bg-gray-300 transition-all ease-in-out',
              )}
            >
              {source.choices.map((choice, index) => (
                <button
                  key={choice.label}
                  className={clsx(
                    source.label === selectedSource ? 'translate-y-0' : '-translate-y-16',
                    'group flex w-full cursor-pointer items-center justify-between border-white bg-inherit p-1 text-gray-900 transition-all ease-in-out first:rounded-t-md last:rounded-b-md [&:not(:last-child)]:border-b',
                    index >= 4 ? 'border-t border-gray-400' : '',
                  )}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelectedChoice(source.label, choice.value);
                  }}
                >
                  <Label className='group-hover:border-midnight w-full cursor-pointer truncate rounded-md border-2 border-gray-300 p-2 text-left text-sm font-normal group-hover:border-dashed'>
                    {choice.label}
                  </Label>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
